import db from '../data/db';
import { BADGES } from '../data/badges';
import { CardState } from '../data/fsrs';

const LANGUAGE_DECK_KEYS = new Set(['gcse-french', 'gcse-spanish', 'gcse-german']);
const MATURE_STABILITY_DAYS = 21;

// Build the evaluation context. `session` describes the session just completed:
//   { cardsReviewed, correctCount, hadPriorStudy, daysSinceLastStudy, hour }
export async function buildContext({ streak, session }) {
  const allStats = await db.stats.toArray();
  const totalCardsReviewed = allStats.reduce((sum, s) => sum + (s.cardsStudied || 0), 0);

  const allDecks = await db.decks.toArray();
  const allCards = await db.cards.toArray();

  // Mastery: a deck counts as "mastered" when every card is in REVIEW state
  // with stability above the mature threshold.
  let gcseDecksMastered = 0;
  const languagesStudied = new Set();
  for (const deck of allDecks) {
    if (!deck.systemDeckKey) continue;
    const cards = allCards.filter(c => c.deckId === deck.id);
    if (cards.length === 0) continue;

    const allMature = cards.every(c =>
      c.fsrs?.state === CardState.REVIEW &&
      (c.fsrs?.stability ?? 0) >= MATURE_STABILITY_DAYS
    );
    if (allMature && deck.isSystem) gcseDecksMastered++;

    if (LANGUAGE_DECK_KEYS.has(deck.systemDeckKey)) {
      const anyStudied = cards.some(c => c.fsrs?.state && c.fsrs.state !== CardState.NEW);
      if (anyStudied) languagesStudied.add(deck.systemDeckKey);
    }
  }

  const sessionAccuracy = session.cardsReviewed > 0
    ? session.correctCount / session.cardsReviewed
    : 0;

  return {
    totalCardsReviewed,
    longestStreak: streak.longest || 0,
    currentStreak: streak.current || 0,
    sessionPerfect: session.cardsReviewed >= 10 && sessionAccuracy === 1,
    gcseDecksMastered,
    studiedAtHour: session.hour,
    comeback: session.hadPriorStudy && session.daysSinceLastStudy >= 7,
    languageDecksStudied: languagesStudied.size,
  };
}

// Returns badge ids that are now unlocked but weren't before.
export function evaluateNewUnlocks(context, alreadyUnlocked) {
  const newlyUnlocked = [];
  for (const badge of BADGES) {
    if (alreadyUnlocked[badge.id]) continue;
    try {
      if (badge.check(context)) newlyUnlocked.push(badge.id);
    } catch {
      // Defensive: a malformed check shouldn't break study flow.
    }
  }
  return newlyUnlocked;
}
